import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Logo } from "@/components/logo";
import { pickBg } from "@/assets/generated";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CONTACT_EMAIL } from "@/lib/seo/site";
import { WHATSAPP_CONTACT_NAME, WHATSAPP_CONTACT_PHONE, buildWhatsAppUrl } from "@/lib/whatsapp-contact";
import { LanguageSwitcher } from "@/components/language-switcher";
import { Loader2, CheckCircle2 } from "lucide-react";
import { MobileFooter } from "./mobile/mobile-footer";
import { BeLeadSignature } from "@/components/BeLeadSignature";

function NewsletterForm() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const r = await fetch("/api/public/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, kind: "newsletter", source: "footer", website, locale: i18n.resolvedLanguage?.startsWith("en") ? "en" : "it" }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || "Error");
      setDone(true);
    } catch (e: any) {
      setError(e?.message ?? "Error");
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="flex items-center gap-2 text-sm text-success">
      <CheckCircle2 className="h-4 w-4" /> {t("footer.confirmed")}
    </div>
  );
  return (
    <form onSubmit={submit} className="flex flex-col gap-2 sm:flex-row">
      <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("footer.emailPlaceholder")} className="bg-white/5 text-white border-white/10 placeholder:text-white/40" />
      <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden />
      <Button type="submit" disabled={loading} variant="secondary">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("footer.subscribe")}
      </Button>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </form>
  );
}

export function MarketingFooter() {
  const { t } = useTranslation();
  const cols = [
    {
      title: t("footer.columns.product"),
      items: [
        { label: t("footer.links.features"), href: "/#features" },
        { label: t("footer.links.pricing"), href: "/#pricing" },
        { label: t("footer.links.demo"), href: "/#demo" },
        { label: t("footer.links.security"), href: "/sicurezza" },
      ],
    },
    {
      title: t("footer.columns.company"),
      items: [
        { label: t("footer.links.about"), href: "/chi-siamo" },
        { label: t("footer.links.blog"), href: "/blog" },
        { label: t("footer.links.contact"), href: "/contatti" },
        { label: t("footer.links.glossary"), href: "/glossario" },
      ],
    },
    {
      title: t("footer.columns.legal"),
      items: [
        { label: t("footer.links.privacy"), href: "/privacy" },
        { label: t("footer.links.terms"), href: "/termini" },
        { label: t("footer.links.refunds"), href: "/rimborsi" },
        { label: t("footer.links.gdpr"), href: "/gdpr" },
      ],
    },
  ];
  return (
    <>
    <div className="md:hidden"><MobileFooter /></div>
    <footer className="relative overflow-hidden border-t border-white/10 bg-black text-white/70 pb-safe hidden md:block">
      <img src={pickBg(46)} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-10" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/95 to-black" />
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="aurora-orb absolute -left-40 -top-40" style={{ width: 560, height: 560, background: "radial-gradient(circle at 30% 30%, hsl(var(--aurora-1) / 0.45), transparent 60%)" }} />
        <div className="aurora-orb absolute right-[-10rem] bottom-[-8rem]" style={{ width: 520, height: 520, background: "radial-gradient(circle at 30% 30%, hsl(var(--aurora-2) / 0.4), transparent 60%)", animationDelay: "5s" }} />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <Logo variant="light" size={44} />
            <p className="mt-4 max-w-xs text-sm text-white/60">
              {t("footer.tagline")}
            </p>
            <div className="mt-5 max-w-sm">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-white">{t("footer.newsletter")}</p>
              <NewsletterForm />
            </div>
            <p className="mt-4 text-xs text-white/50">
              {t("footer.support")}: <a href={`mailto:${CONTACT_EMAIL}`} className="text-white/80 hover:text-accent">{CONTACT_EMAIL}</a>
            </p>
            <p className="mt-2 text-xs text-white/50">
              {t("footer.whatsapp")}: <a href={buildWhatsAppUrl()} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-accent">{WHATSAPP_CONTACT_NAME} · {WHATSAPP_CONTACT_PHONE}</a>
            </p>
            <div className="mt-5">
              <LanguageSwitcher variant="dark" />
            </div>
          </div>
          {cols.map((c, ci) => (
            <div key={c.title} className="border-t border-white/10 pt-5">
              <div className="flex items-center gap-2.5">
                <span className="font-editorial text-[13px] italic text-accent">{String(ci + 1).padStart(2, "0")}</span>
                <span className="h-px w-6 bg-white/20" aria-hidden />
              </div>
              <h3 className="mt-2 font-editorial text-lg font-semibold text-white">{c.title}</h3>
              <ul className="mt-4 space-y-2">
                {c.items.map((it) => (
                  <li key={it.label}>
                    <a href={it.href} className="text-sm text-white/60 transition-colors hover:text-accent">
                      {it.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 md:flex-row">
          <p className="text-xs text-white/50">LexAIPro © {new Date().getFullYear()} · lexaipro.dev</p>
          <div className="flex flex-wrap items-center gap-4 text-xs text-white/50">
            <Link to="/sicurezza" className="transition-colors hover:text-white">🇪🇺 {t("footer.badges.euData")}</Link>
            <Link to="/gdpr" className="transition-colors hover:text-white">🔐 {t("footer.badges.gdpr")}</Link>
            <Link to="/conformita-ai-act" className="transition-colors hover:text-white">🤖 {t("footer.badges.aiAct")}</Link>
          </div>
          <div className="flex flex-col items-center gap-2 md:flex-row md:gap-5">
            <Link to="/login" className="text-xs text-white/60 hover:text-white">{t("footer.login")} →</Link>
            <BeLeadSignature tone="onDark" />
          </div>
        </div>
      </div>
    </footer>
    </>
  );
}
